import { type ReactNode, useCallback, useMemo, useState } from "react";

import { StatusPopup } from "@vega-core/components/StatusPopup/StatusPopup";
import { StatusPopupContext } from "@vega-core/components/StatusPopup/StatusPopupContext";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { type SetStatusOptions } from "./types";

const ANIMATION_TIME = 300;

export const StatusPopupProvider = ({ children }: { children: ReactNode }) => {
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [type, setType] = useState<"error" | "success" | null>(null);

  const closePopup = useCallback(() => {
    setType(null);

    setTimeout(() => {
      if (isFalsy(type)) {
        setTitle("");
        setDescription("");
      }
    }, ANIMATION_TIME);
  }, [type]);

  const setMessage = useCallback((options?: SetStatusOptions) => {
    setTitle(options?.title ?? "");
    setDescription(options?.description ?? "");
  }, []);

  const setError = useCallback(
    (options?: SetStatusOptions) => {
      setMessage(options);
      setType("error");
    },
    [setMessage],
  );

  const setSuccess = useCallback(
    (options?: SetStatusOptions) => {
      setMessage(options);
      setType("success");
    },
    [setMessage],
  );

  const providerValue = useMemo(
    () => ({
      closePopup,
      isError: type === "error",
      setError,
      setSuccess,
    }),
    [closePopup, setError, setSuccess, type],
  );

  return (
    <StatusPopupContext.Provider value={providerValue}>
      {children}

      <StatusPopup
        description={description}
        onClose={closePopup}
        open={isTruthy(type)}
        title={title}
        type={type}
      />
    </StatusPopupContext.Provider>
  );
};
